import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { hydrate } from "@/lib";

export type AuthGateStep =
  | "options"
  | "email-login"
  | "otp-login"
  | "profile-gate";

export type ProfileGateMode = "required" | "optional";

export interface AuthGateState {
  isOpen: boolean;
  currentStep: AuthGateStep;
  profileGateMode: ProfileGateMode;
  reason: string;
  source: string | null;
  returnTo: string | null;
  extraQueryParams: Record<string, any> | null;
  /** When true, email-login back closes the modal instead of returning to options. */
  skippedOptionsStep: boolean;
}

const initialState: AuthGateState = {
  isOpen: false,
  currentStep: "options",
  profileGateMode: "required",
  reason: "",
  source: null,
  returnTo: null,
  extraQueryParams: null,
  skippedOptionsStep: false
};

export interface OpenGuestLoginPayload {
  extraQueryParams?: Record<string, any>;
  initialStep?: AuthGateStep;
}

export interface OpenProfileGatePayload {
  mode: ProfileGateMode;
  reason: string;
  source: string;
  returnTo: string | null;
}

export const authGateSlice = createSlice({
  name: "authGate",
  initialState,
  reducers: {
    openGuestLogin: (
      state,
      action: PayloadAction<OpenGuestLoginPayload | undefined>
    ) => {
      state.isOpen = true;
      state.currentStep = action?.payload?.initialStep ?? "options";
      state.extraQueryParams = action?.payload?.extraQueryParams ?? null;
      state.skippedOptionsStep =
        !!action?.payload?.initialStep &&
        action.payload.initialStep !== "options";
    },
    openProfileGate: (state, action: PayloadAction<OpenProfileGatePayload>) => {
      state.isOpen = true;
      state.currentStep = "profile-gate";
      state.profileGateMode = action.payload.mode;
      state.reason = action.payload.reason;
      state.source = action.payload.source;
      state.returnTo = action.payload.returnTo;
    },
    goToStep: (state, action: PayloadAction<AuthGateStep>) => {
      state.currentStep = action.payload;
    },
    closeAuthGate: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(hydrate, (state, action: PayloadAction<string>) => {
      return {
        ...state,
        ...action.payload[authGateSlice.name]
      };
    });
  }
});

export const { openGuestLogin, openProfileGate, goToStep, closeAuthGate } =
  authGateSlice.actions;
